// React / Next
import { useEffect, useState } from 'react';

// Components
import { Typography } from '@archieai/ui';
import { SEO } from '../SEO/SEO';
import { LegalPortableText } from '../LegalPortableText/LegalPortableText';

// Styles
import styles from './LegalPageContent.module.css';

// Hooks
import { getLegalPageBySlug } from '../../lib/sanity';

// Types
import type { LegalPage } from '../../lib/sanity';

type LegalPageContentProps = {
    slug: string;
    fallbackTitle?: string;
};

export const LegalPageContent = ({ slug, fallbackTitle = 'Loading...' }: LegalPageContentProps) => {
    const [page, setPage] = useState<LegalPage | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        const fetchPage = async () => {
            try {
                setLoading(true);
                const data = await getLegalPageBySlug(slug);
                setPage(data);
            } catch (err) {
                setError('Failed to load page content');
                console.error('Error fetching legal page:', err);
            } finally {
                setLoading(false);
            }
        };

        fetchPage();
    }, [slug]);

    if (loading) {
        return (
            <div className="flex flex-col items-center bg-white pb-[var(--spacing-96)]">
                <div className="flex flex-col gap-[80px] max-w-[806px] w-full px-[var(--spacing-24)] pt-[80px]">
                    <header className="flex flex-col items-center">
                        <Typography as="h1" variant="h1" className="text-blue-1000 text-center">
                            {fallbackTitle}
                        </Typography>
                    </header>
                </div>
            </div>
        );
    }

    if (error || !page) {
        return (
            <div className="flex flex-col items-center bg-white pb-[var(--spacing-96)]">
                <div className="flex flex-col gap-[80px] max-w-[806px] w-full px-[var(--spacing-24)] pt-[80px]">
                    <header className="flex flex-col items-center">
                        <Typography as="h1" variant="h1" className="text-blue-1000 text-center">
                            Page Not Found
                        </Typography>
                        <Typography as="p" variant="h4" className="text-blue-1000 text-center mt-6">
                            {error || 'The requested legal page could not be found.'}
                        </Typography>
                    </header>
                </div>
            </div>
        );
    }

    const hasDescription = page.description && page.description.length > 0;

    return (
        <div className="flex flex-col items-center bg-white pb-[var(--spacing-96)]">
            <SEO
                title={page.seo?.metaTitle || page.title}
                description={page.seo?.metaDescription || ''}
                canonicalUrl={page.seo?.canonicalUrl || `/${page.slug}`}
            />

            {/* Main Container */}
            <div className="flex flex-col gap-[80px] max-w-[806px] w-full px-[var(--spacing-24)] pt-[80px]">
                {/* Header Section */}
                <header className="flex flex-col items-center gap-[24px]">
                    <Typography as="h1" variant="h1" className="text-blue-1000 text-center w-full">
                        {page.title}
                    </Typography>
                    {hasDescription && (
                        <LegalPortableText
                            value={page.description!}
                            className={`${styles.description} text-blue-1000 text-left`}
                        />
                    )}
                </header>

                {/* Content Sections */}
                <article className="flex flex-col gap-[56px]">
                    {page.sections.map((section) => (
                        <section key={section._key} className="flex flex-col gap-[24px] text-blue-1000">
                            <h2 className="font-heading text-[40px] leading-[48px] tracking-[-0.5px] font-light">
                                {section.heading}
                            </h2>
                            <LegalPortableText value={section.content} className={styles.content} />
                        </section>
                    ))}
                </article>
            </div>
        </div>
    );
};

